import {
  Button,
  Popover,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import { useState } from "react";

const ColumnFilter = ({
  anchorE2,
  setAnchorE2,
  totalColumns,
  tableHeaderColumns,
  standardHeaderColumns,
  onApplyFilter,
  updateAlert,
  sourceData,
  useCase,
}) => {
  const [selectedColumns, setSelectedColumns] = useState(tableHeaderColumns);

  const customSort = (a, b) => {
    const A = a?.label ?? a;
    const B = b?.label ?? b;

    if (A == null || B == null) return 0;

    const strA = String(A);
    const strB = String(B);

    const getType = (char) => {
      if (/[^a-zA-Z0-9]/.test(char)) return 0; // special char
      if (/[0-9]/.test(char)) return 1; // number
      if (/[A-Z]/.test(char)) return 2; // uppercase
      if (/[a-z]/.test(char)) return 3; // lowercase
      return 4;
    };

    const typeA = getType(strA[0]);
    const typeB = getType(strB[0]);

    // 1️⃣ Sort by type
    if (typeA !== typeB) {
      return typeA - typeB;
    }

    // 2️⃣ Same letters, different case → uppercase first
    if (strA.toLowerCase() === strB.toLowerCase() && strA !== strB) {
      return strA < strB ? -1 : 1;
    }

    // 3️⃣ Normal sort (handles numbers properly)
    return strA.localeCompare(strB, undefined, { numeric: true });
  };

  let COLUMNS;
  if (useCase === "AttributeEnrichment") {
    const FINAL_COLUMNS =
      totalColumns
        .filter((column) => {
          return (
            column.name !==
              (sourceData?.data?.dropdown?.table_primary_key || "") &&
            column.name !== "UPC"
          );
        })
        .map((item) => {
          return {
            label: item.label,
            name: item.name,
          };
        }) || [];

    COLUMNS = (FINAL_COLUMNS || []).slice().sort(customSort);
  } else {
    COLUMNS = ([{ label: "All", name: "all" }, ...totalColumns] || [])
      .slice()
      .sort(customSort);
  }

  const open = Boolean(anchorE2);

  const handleSelectChange = (newValue) => {
    if (useCase === "AttributeEnrichment") {
      if (newValue.length <= 6) {
        setSelectedColumns(newValue);
      } else {
        updateAlert({
          open: true,
          message: "Cannot filter more that 6 columns",
          severity: "Failed",
          closeFlag: true,
        });
      }
    } else {
      const result = newValue.some((item) => {
        return item.name === "all";
      });
      if (result) {
        setSelectedColumns(COLUMNS);
      } else {
        setSelectedColumns(newValue);
      }
    }
  };

  const handleApply = () => {
    onApplyFilter(selectedColumns.filter((item) => item.name !== "all"));
    setAnchorE2(null);
    setSelectedColumns(selectedColumns);
  };

  const handleClose = () => {
    setAnchorE2(null);
    if (sourceData.current_columns && sourceData.current_columns.length > 0) {
      setSelectedColumns(sourceData.current_columns);
    } else {
      setSelectedColumns(tableHeaderColumns);
    }
  };

  const handleReset = () => {
    onApplyFilter(standardHeaderColumns);
    setAnchorE2(null);
    setSelectedColumns(standardHeaderColumns);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorE2}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <div className="custom-style">
        {/* Select Columns */}
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={COLUMNS}
          value={selectedColumns || []}
          onChange={(e, newVal) => handleSelectChange(newVal)}
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          renderOption={(props, option, { selected }) => {
            let isExisting;
            if (useCase === "AttributeEnrichment") {
              isExisting = selectedColumns.some((col) => {
                return col.label === option.label && col.name === option.name;
              });
            } else {
              isExisting = selectedColumns.some((col) => {
                return col.name === option.name;
              });
            }

            return (
              <li {...props}>
                <Checkbox
                  checked={isExisting}
                  sx={{
                    transform: "scale(.9)", // scale up (1 = default size)
                    padding: "2px", // optional: reduce padding
                  }}
                />
                {option.label}
              </li>
            );
          }}
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "4px",
            maxHeight: 200, // restrict height
            overflowY: "auto", // scroll when too many
            width: "100%",
            paddingTop: "10px",
            paddingRight: "5px",
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.name}
                label={option.label}
                {...getTagProps({ index })}
                size="small"
              />
            ))
          }
          sx={{
            fontSize: "14px", // base font size
            "& .MuiInputBase-root": {
              padding: "2px 10px 2px 10px",
              fontSize: "16px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "14px",
            },
            "& .MuiAutocomplete-endAdornment": {
              top: "calc(100% - 18px)", // center the dropdown icon
            },
          }}
          slotProps={{
            popper: {
              sx: {
                "& .MuiAutocomplete-listbox": {
                  fontSize: "14px", // ⬅️ dropdown font size
                  padding: "2px 0",
                },
                "& .MuiAutocomplete-option": {
                  padding: "2px 2px",
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Columns" />
          )}
        />

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={!selectedColumns || selectedColumns.length === 0}
            style={{
              marginTop: "10px",
              backgroundColor: "#46596A",
              opacity:
                selectedColumns && selectedColumns.length > 0 ? "1" : "0.6",
              textTransform: "capitalize",
              padding: "6px 10px",
              cursor:
                selectedColumns && selectedColumns.length > 0
                  ? "pointer"
                  : "not-allowed !important",
              color: "white",
            }}
          >
            Apply Selection
          </Button>
          <Button
            onClick={handleReset}
            variant="contained"
            style={{
              marginTop: "10px",
              backgroundColor: "#46596A",
              textTransform: "capitalize",
              padding: "6px 10px",
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export default ColumnFilter;
